import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import routeUrls from "../COMPONENTS/navbar/navLink";
import {
  Recipe,
  PlanMeal,
  FoodKind,
  KIND_ORDER,
  KIND_META,
  RECIPES,
  BUILTIN_IDS,
  blankRecipe,
  pickRandom,
  buildShoppingList,
  cookEstimate,
} from "../COMPONENTS/food/recipes";
import { RecipeEditor } from "../COMPONENTS/food/RecipeEditor";
import { usePageBackground } from "../COMPONENTS/usePageBackground";
import "../CSS/food.scss";

const STORE_KEY = "food-planner-v1";
const RECIPES_KEY = "food-recipes-v1";
const DEFAULT_COUNT = 5;
const DEFAULT_SERVINGS = 4;

interface Saved {
  count: number;
  meals: { id: string; servings: number }[];
  checked: string[];
  kinds: FoodKind[];
  defaultServings: number;
  favorites: string[];
  matlada: boolean;
  matladaCount: number;
}

const load = (): Partial<Saved> | null => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clampServings = (n: number) => Math.max(1, Math.min(12, Math.round(n) || 1));

export const FoodPlannerView = () => {
  usePageBackground();
  const [count, setCount] = useState(DEFAULT_COUNT);
  const [meals, setMeals] = useState<PlanMeal[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [kinds, setKinds] = useState<Set<FoodKind>>(new Set(KIND_ORDER));
  const [defaultServings, setDefaultServings] = useState(DEFAULT_SERVINGS);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [matlada, setMatlada] = useState(false);
  const [matladaCount, setMatladaCount] = useState(2); // extra lunch-box portions
  const [showPrefs, setShowPrefs] = useState(false);
  // custom + overridden recipes (by id), stored separately from the weekly plan
  const [customs, setCustoms] = useState<Record<string, Recipe>>(() => {
    try {
      const raw = localStorage.getItem(RECIPES_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [editing, setEditing] = useState<Recipe | null>(null);
  const [showLibrary, setShowLibrary] = useState(false);

  // built-ins merged with custom/overridden recipes
  const recipes = useMemo(() => {
    const m = new Map<string, Recipe>(RECIPES.map((r) => [r.id, r]));
    Object.values(customs).forEach((r) => m.set(r.id, r));
    return Array.from(m.values());
  }, [customs]);
  const byId = useMemo(() => new Map(recipes.map((r) => [r.id, r])), [recipes]);
  const lookup = (id: string) => byId.get(id);

  useEffect(() => {
    try {
      localStorage.setItem(RECIPES_KEY, JSON.stringify(customs));
    } catch {
      /* ignore */
    }
  }, [customs]);

  const kindList = () => Array.from(kinds);
  const favList = () => Array.from(favorites);

  // restore or generate a first week on mount
  useEffect(() => {
    const s = load();
    if (s) {
      if (typeof s.defaultServings === "number") setDefaultServings(clampServings(s.defaultServings));
      if (Array.isArray(s.kinds) && s.kinds.length) setKinds(new Set(s.kinds));
      if (Array.isArray(s.favorites)) setFavorites(new Set(s.favorites));
      if (typeof s.matlada === "boolean") setMatlada(s.matlada);
      if (typeof s.matladaCount === "number") setMatladaCount(Math.max(1, Math.min(6, s.matladaCount)));
      if (Array.isArray(s.checked)) setChecked(new Set(s.checked));
      // new shape (meals with servings) or legacy (mealIds)
      const legacy = (s as any).mealIds as string[] | undefined;
      const entries = Array.isArray(s.meals)
        ? s.meals
        : Array.isArray(legacy)
        ? legacy.map((id) => ({ id, servings: s.defaultServings || DEFAULT_SERVINGS }))
        : [];
      const restored = entries
        .map((e) => {
          const r = lookup(e.id);
          return r ? { recipe: r, servings: clampServings(e.servings) } : null;
        })
        .filter((m): m is PlanMeal => !!m);
      if (restored.length) {
        setCount(s.count || restored.length);
        setMeals(restored);
        return;
      }
    }
    setMeals(pickRandom(recipes, DEFAULT_COUNT).map((r) => ({ recipe: r, servings: DEFAULT_SERVINGS })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // persist on any change
  useEffect(() => {
    const data: Saved = {
      count,
      meals: meals.map((m) => ({ id: m.recipe.id, servings: m.servings })),
      checked: Array.from(checked),
      kinds: Array.from(kinds),
      defaultServings,
      favorites: Array.from(favorites),
      matlada,
      matladaCount,
    };
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(data));
    } catch {
      /* storage unavailable — ignore */
    }
  }, [count, meals, checked, kinds, defaultServings, favorites, matlada, matladaCount]);

  const groups = useMemo(() => buildShoppingList(meals), [meals]);
  const totalItems = groups.reduce((s, g) => s + g.items.length, 0);
  const doneItems = groups.reduce((s, g) => s + g.items.filter((it) => checked.has(it.key)).length, 0);

  // servings a new meal gets: household + lunch boxes (when matlåda is on)
  const newServings = () => clampServings(defaultServings + (matlada ? matladaCount : 0));
  const entryOf = (r: Recipe): PlanMeal => ({ recipe: r, servings: newServings() });
  const genOpts = (exclude: string[] = []) => ({
    exclude,
    kinds: kindList(),
    favorites: favList(),
    matladaOnly: matlada,
  });

  const shuffleWeek = () => setMeals(pickRandom(recipes, count, genOpts()).map(entryOf));

  const swapMeal = (idx: number) =>
    setMeals((cur) => {
      const [next] = pickRandom(recipes, 1, genOpts(cur.map((m) => m.recipe.id)));
      if (!next) return cur;
      return cur.map((m, k) => (k === idx ? { recipe: next, servings: m.servings } : m));
    });

  const removeMeal = (idx: number) => setMeals((cur) => cur.filter((_, k) => k !== idx));

  const addMeal = () =>
    setMeals((cur) => {
      const [next] = pickRandom(recipes, 1, genOpts(cur.map((m) => m.recipe.id)));
      return next ? [...cur, entryOf(next)] : cur;
    });

  const addRecipe = (r: Recipe) =>
    setMeals((cur) => (cur.some((m) => m.recipe.id === r.id) ? cur : [...cur, entryOf(r)]));

  const setMealServings = (idx: number, n: number) =>
    setMeals((cur) => cur.map((m, k) => (k === idx ? { ...m, servings: clampServings(n) } : m)));

  const setCountAndFit = (n: number) => {
    const clamped = Math.max(1, Math.min(7, n));
    setCount(clamped);
    setMeals((cur) => {
      if (clamped <= cur.length) return cur.slice(0, clamped);
      const extra = pickRandom(recipes, clamped - cur.length, genOpts(cur.map((m) => m.recipe.id))).map(entryOf);
      return [...cur, ...extra];
    });
  };

  const toggleKind = (k: FoodKind) =>
    setKinds((cur) => {
      const next = new Set(cur);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next.size ? next : cur; // keep at least one selected
    });

  const toggleFav = (id: string) =>
    setFavorites((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggle = (key: string) =>
    setChecked((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const clearChecked = () => setChecked(new Set());

  const copyList = () => {
    const text = groups
      .map((g) => `${g.cat}\n` + g.items.map((it) => `- ${it.name} (${it.qty} ${it.unit})`).join("\n"))
      .join("\n\n");
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  const favoriteRecipes = favList()
    .map(lookup)
    .filter((r): r is Recipe => !!r);

  // ---- recipe editing ----
  const saveRecipe = (r: Recipe) => {
    setCustoms((c) => ({ ...c, [r.id]: r }));
    setMeals((cur) => cur.map((m) => (m.recipe.id === r.id ? { ...m, recipe: r } : m)));
    setEditing(null);
  };
  const resetRecipe = (id: string) => {
    setCustoms((c) => {
      const n = { ...c };
      delete n[id];
      return n;
    });
    const builtin = RECIPES.find((r) => r.id === id);
    if (builtin) setMeals((cur) => cur.map((m) => (m.recipe.id === id ? { ...m, recipe: builtin } : m)));
    setEditing(null);
  };
  const deleteRecipe = (id: string) => {
    setCustoms((c) => {
      const n = { ...c };
      delete n[id];
      return n;
    });
    setMeals((cur) => cur.filter((m) => m.recipe.id !== id));
    setFavorites((f) => {
      const n = new Set(f);
      n.delete(id);
      return n;
    });
    setEditing(null);
  };

  return (
    <div className="food">
      <div className="food__header">
        <NavLink to={routeUrls.HOME} className="food__back">← Hem</NavLink>
        <h1 className="food__title">Veckomatsedel</h1>
        <span className="food__sub">Förslag på veckans middagar + automatisk inköpslista</span>
      </div>

      <div className="food__controls">
        <div className="food__stepper">
          <button className="food__step-btn" onClick={() => setCountAndFit(count - 1)} aria-label="Färre">−</button>
          <span className="food__step-val">{count} middagar</span>
          <button className="food__step-btn" onClick={() => setCountAndFit(count + 1)} aria-label="Fler">+</button>
        </div>
        <button className="food__shuffle" onClick={shuffleWeek}>🎲 Slumpa vecka</button>
        <button
          className={`food__prefs-toggle${showPrefs ? " food__prefs-toggle--open" : ""}`}
          onClick={() => setShowPrefs((v) => !v)}
        >
          ⚙️ Preferenser
        </button>
        <button className="food__prefs-toggle" onClick={() => setShowLibrary(true)}>📖 Recept</button>
      </div>

      {showPrefs && (
        <div className="food__prefs">
          <div className="food__pref-block">
            <span className="food__pref-label">Typer av mat jag gillar</span>
            <div className="food__kind-chips">
              {KIND_ORDER.map((k) => (
                <button
                  key={k}
                  className={`food__kind-chip${kinds.has(k) ? " food__kind-chip--on" : ""}`}
                  onClick={() => toggleKind(k)}
                >
                  {KIND_META[k].icon} {KIND_META[k].label}
                </button>
              ))}
            </div>
          </div>
          <div className="food__pref-block">
            <span className="food__pref-label">Standard antal matlådor (nya rätter)</span>
            <div className="food__stepper">
              <button className="food__step-btn" onClick={() => setDefaultServings((s) => clampServings(s - 1))}>−</button>
              <span className="food__step-val">{defaultServings} lådor</span>
              <button className="food__step-btn" onClick={() => setDefaultServings((s) => clampServings(s + 1))}>+</button>
            </div>
          </div>
          <div className="food__pref-block">
            <span className="food__pref-label">🍱 Matlådor</span>
            <div className="food__kind-chips">
              <button
                className={`food__kind-chip${matlada ? " food__kind-chip--on" : ""}`}
                onClick={() => setMatlada((v) => !v)}
              >
                {matlada ? "På" : "Av"} — laga extra
              </button>
            </div>
            {matlada && (
              <div className="food__stepper">
                <button className="food__step-btn" onClick={() => setMatladaCount((c) => Math.max(1, c - 1))}>−</button>
                <span className="food__step-val">+{matladaCount} matlådor</span>
                <button className="food__step-btn" onClick={() => setMatladaCount((c) => Math.min(6, c + 1))}>+</button>
              </div>
            )}
            {matlada && (
              <span className="food__pref-hint">Föreslår rätter som funkar återuppvärmda, {defaultServings}+{matladaCount} lådor.</span>
            )}
          </div>
          <div className="food__pref-block food__pref-block--wide">
            <span className="food__pref-label">⭐ Favoriter {favoriteRecipes.length > 0 && `(${favoriteRecipes.length})`}</span>
            {favoriteRecipes.length === 0 ? (
              <span className="food__pref-hint">Stjärnmärk rätter nedan så prioriteras de i förslagen.</span>
            ) : (
              <div className="food__fav-list">
                {favoriteRecipes.map((r) => (
                  <div key={r.id} className="food__fav">
                    <span>{r.emoji} {r.name}</span>
                    <button className="food__link-btn" onClick={() => addRecipe(r)}>+ Lägg till</button>
                    <button className="food__icon-btn" title="Ta bort favorit" onClick={() => toggleFav(r.id)}>⭐</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="food__grid">
        <section className="food__meals">
          <h2 className="food__section-title">Veckans middagar</h2>
          <span className="food__section-hint">🍳 "Att laga" är en uppskattning av huvudråvaran — för kött/fisk ca 167 g per matlåda (1 kg ≈ 6 matlådor).</span>
          <div className="food__meal-list">
            {meals.map((m, idx) => (
              <div key={`${m.recipe.id}-${idx}`} className="food__meal">
                <span className="food__meal-emoji">{m.recipe.emoji}</span>
                <div className="food__meal-body">
                  <span className="food__meal-name">{m.recipe.name}</span>
                  <div className="food__meal-tags">
                    <span className="food__tag food__tag--kind">
                      {KIND_META[m.recipe.kind].icon} {KIND_META[m.recipe.kind].label}
                    </span>
                    {m.recipe.tags.map((t) => (
                      <span key={t} className="food__tag">{t}</span>
                    ))}
                  </div>
                  {(() => {
                    const est = cookEstimate(m.recipe, m.servings);
                    return est ? (
                      <span className="food__meal-cook" title="Uppskattning: ca 167 g huvudråvara per matlåda">
                        🍳 Att laga ca <strong>{est.amount} {est.name}</strong>
                      </span>
                    ) : null;
                  })()}
                </div>
                <div className="food__meal-servings">
                  <button className="food__srv-btn" title="Färre matlådor" onClick={() => setMealServings(idx, m.servings - 1)}>−</button>
                  <span className="food__srv-val">{m.servings}<span className="food__srv-unit">lådor</span></span>
                  <button className="food__srv-btn" title="Fler matlådor" onClick={() => setMealServings(idx, m.servings + 1)}>+</button>
                </div>
                <div className="food__meal-actions">
                  <button
                    className={`food__icon-btn${favorites.has(m.recipe.id) ? " food__icon-btn--fav" : ""}`}
                    title={favorites.has(m.recipe.id) ? "Ta bort favorit" : "Favoritmarkera"}
                    onClick={() => toggleFav(m.recipe.id)}
                  >
                    {favorites.has(m.recipe.id) ? "⭐" : "☆"}
                  </button>
                  <button className="food__icon-btn" title="Justera recept" onClick={() => setEditing(m.recipe)}>✏️</button>
                  <button className="food__icon-btn" title="Byt rätt" onClick={() => swapMeal(idx)}>🔀</button>
                  <button className="food__icon-btn food__icon-btn--del" title="Ta bort" onClick={() => removeMeal(idx)}>×</button>
                </div>
              </div>
            ))}
            {meals.length < 7 && <button className="food__add" onClick={addMeal}>+ Lägg till rätt</button>}
            {meals.length === 0 && <p className="food__empty">Slumpa en vecka för att börja.</p>}
          </div>
        </section>

        <section className="food__shopping">
          <div className="food__shopping-head">
            <h2 className="food__section-title">Inköpslista</h2>
            <span className="food__count">{doneItems}/{totalItems}</span>
          </div>
          {totalItems === 0 ? (
            <p className="food__empty">Lägg till middagar så byggs listan här.</p>
          ) : (
            <>
              <div className="food__shopping-actions">
                <button className="food__link-btn" onClick={copyList}>📋 Kopiera</button>
                {doneItems > 0 && <button className="food__link-btn" onClick={clearChecked}>Nollställ bockar</button>}
              </div>
              {groups.map((g) => (
                <div key={g.cat} className="food__cat">
                  <h3 className="food__cat-title">{g.cat}</h3>
                  <ul className="food__items">
                    {g.items.map((it) => {
                      const on = checked.has(it.key);
                      return (
                        <li key={it.key} className={`food__item${on ? " food__item--done" : ""}`}>
                          <label className="food__item-label">
                            <input type="checkbox" checked={on} onChange={() => toggle(it.key)} />
                            <span className="food__item-name">{it.name}</span>
                          </label>
                          <span className="food__item-qty">{it.qty} {it.unit}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </>
          )}
        </section>
      </div>

      {showLibrary && (
        <div className="food__overlay" onClick={() => setShowLibrary(false)}>
          <div className="food__modal" onClick={(e) => e.stopPropagation()}>
            <div className="food__modal-head">
              <h3 className="food__modal-title">Recept ({recipes.length})</h3>
              <button className="food__modal-close" onClick={() => setShowLibrary(false)} aria-label="Stäng">×</button>
            </div>
            <div className="food__modal-body">
              <button className="food__add" onClick={() => setEditing(blankRecipe())}>+ Ny rätt</button>
              <div className="food__lib-list">
                {[...recipes]
                  .sort((a, b) => a.name.localeCompare(b.name, "sv"))
                  .map((r) => (
                    <div key={r.id} className="food__lib-row">
                      <span className="food__lib-name">
                        {r.emoji} {r.name} <span className="food__lib-kind">{KIND_META[r.kind].icon}</span>
                        {!BUILTIN_IDS.has(r.id) && <span className="food__tag">Egen</span>}
                        {BUILTIN_IDS.has(r.id) && customs[r.id] && <span className="food__tag">Ändrad</span>}
                      </span>
                      <button className="food__link-btn" onClick={() => addRecipe(r)}>+ Vecka</button>
                      <button className="food__icon-btn" title="Justera" onClick={() => setEditing(r)}>✏️</button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <RecipeEditor
          initial={editing}
          isBuiltin={BUILTIN_IDS.has(editing.id)}
          onSave={saveRecipe}
          onCancel={() => setEditing(null)}
          onReset={BUILTIN_IDS.has(editing.id) && customs[editing.id] ? () => resetRecipe(editing.id) : undefined}
          onDelete={!BUILTIN_IDS.has(editing.id) ? () => deleteRecipe(editing.id) : undefined}
        />
      )}
    </div>
  );
};
